import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, projectCostsAPI, projectDocumentsAPI } from '../../../services/api';
import { Badge } from '../../../components/common/Badge';
import { Button } from '../../../components/common/Button';
import { Table } from '../../../components/common/Table';
import { CostEntryForm } from './CostEntryForm';
import { ProjectCostInvoiceModal } from '../accounting/InvoicePDF';
import {
  ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon,
  CubeIcon, UserGroupIcon, ChartBarIcon,
  CurrencyRupeeIcon, ArrowUpCircleIcon, ArrowDownCircleIcon,
  DocumentTextIcon, CheckCircleIcon, PrinterIcon,
  FolderOpenIcon, ArrowDownTrayIcon, PaperClipIcon,
  PhotoIcon, TableCellsIcon, FolderIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ProjectStageFlowchart, PROJECT_STAGES } from './ProjectStageFlowchart';

const TABS = [
  { id: 'overview',      label: 'Overview',        icon: ChartBarIcon },
  { id: 'material',      label: 'Materials',        icon: CubeIcon },
  { id: 'subcontractor', label: 'Sub-Contractors',  icon: UserGroupIcon },
  { id: 'pl',            label: 'P&L Summary',      icon: CurrencyRupeeIcon },
  { id: 'docs',          label: 'Project Docs',     icon: FolderOpenIcon },
];

export function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project,  setProject]  = useState(null);
  const [costs,    setCosts]    = useState([]);
  const [summary,  setSummary]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('overview');
  const [formOpen, setFormOpen] = useState(false);
  const [formType, setFormType] = useState('material');
  const [editing,  setEditing]  = useState(null);
  const [invoiceOpen,  setInvoiceOpen]  = useState(false);
  const [invoiceRecord, setInvoiceRecord] = useState(null);
  const [invoiceType,   setInvoiceType]   = useState('material');
  const [allDocs,      setAllDocs]      = useState([]);
  const [docsLoading,  setDocsLoading]  = useState(false);

  const loadProject = useCallback(async () => {
    try {
      const r = await projectsAPI.getById(id);
      setProject(r.data);
    } catch { toast.error('Failed to load project'); }
  }, [id]);

  const loadAllDocs = useCallback(async () => {
    setDocsLoading(true);
    try {
      const res = await projectDocumentsAPI.getAll(id);
      setAllDocs(res.data || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setDocsLoading(false); }
  }, [id]);

  const loadCosts = useCallback(async () => {
    try {
      const [costsRes, sumRes] = await Promise.allSettled([
        projectCostsAPI.getAll(id, tab === 'pl' || tab === 'overview' ? {} : { costType: tab }),
        projectCostsAPI.getSummary(id),
      ]);
      if (costsRes.status === 'fulfilled') setCosts(costsRes.value.data || []);
      if (sumRes.status  === 'fulfilled') setSummary(sumRes.value.data || []);
    } catch { toast.error('Failed to load costs'); }
    finally { setLoading(false); }
  }, [id, tab]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { loadCosts();  }, [loadCosts]);
  useEffect(() => { if (tab === 'docs') loadAllDocs(); }, [tab, loadAllDocs]);

  const openCreate = (type) => { setFormType(type); setEditing(null); setFormOpen(true); };
  const openEdit   = (cost) => { setFormType(cost.costType); setEditing(cost); setFormOpen(true); };

  const handleSave = async (formData) => {
    try {
      editing
        ? await projectCostsAPI.update(id, editing._id, formData)
        : await projectCostsAPI.create(id, formData);
      toast.success(editing ? 'Updated' : 'Added');
      setFormOpen(false);
      loadCosts();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await projectCostsAPI.delete(id, costId); toast.success('Deleted'); loadCosts(); }
    catch { toast.error('Failed to delete'); }
  };

  // P&L calculation
  const budgetedRevenue = project?.budget || 0;
  const materialCost    = summary.find(s => s._id === 'material')?.totalCost    || 0;
  const subconCost      = summary.find(s => s._id === 'subcontractor')?.totalCost || 0;
  const totalCost       = materialCost + subconCost;
  const grossProfit     = budgetedRevenue - totalCost;
  const profitPct       = budgetedRevenue > 0 ? ((grossProfit / budgetedRevenue) * 100).toFixed(1) : 0;
  const matPaid         = summary.find(s => s._id === 'material')?.totalPaid    || 0;
  const subPaid         = summary.find(s => s._id === 'subcontractor')?.totalPaid || 0;
  const totalPaid       = matPaid + subPaid;
  const outstanding     = totalCost - totalPaid;

  // Filter costs by active tab
  const filteredCosts = (tab === 'material' || tab === 'subcontractor')
    ? costs.filter(c => c.costType === tab)
    : costs;

  const costCols = [
    { header: 'Invoice', render: c => (
      <div>
        <p className="font-semibold text-sm text-gray-900">{c.title}</p>
        <p className="text-xs font-mono text-gray-400">{c.invoiceNo || '—'}</p>
      </div>
    )},
    { header: 'Party', render: c => (
      <div>
        <p className="text-sm text-gray-700">{c.vendor?.name || c.vendorName || '—'}</p>
        {c.vendor?.company && <p className="text-xs text-gray-400">{c.vendor.company}</p>}
      </div>
    )},
    { header: 'Date', render: c => (
      <span className="text-xs text-gray-500">{format(new Date(c.invoiceDate), 'dd MMM yyyy')}</span>
    )},
    { header: 'Items', render: c => (
      <span className="text-xs text-gray-500">{c.lineItems?.length || 0} item(s)</span>
    )},
    { header: 'Total', render: c => (
      <div>
        <p className="font-bold text-gray-900 text-sm">{'₹' + Number(c.total || 0).toLocaleString('en-IN')}</p>
        {c.paidAmount > 0 && c.paidAmount < c.total && (
          <p className="text-xs text-amber-600">Paid ₹{Number(c.paidAmount).toLocaleString('en-IN')}</p>
        )}
        {c.paidAmount >= c.total && c.total > 0 && (
          <p className="text-xs text-green-600">Fully paid</p>
        )}
      </div>
    )},
    { header: 'Status', render: c => <Badge status={c.status} /> },
    { header: '', render: c => (
      <div className="flex gap-1">
        <button onClick={() => { setInvoiceRecord(c); setInvoiceType(c.costType); setInvoiceOpen(true); }} className="p-1.5 hover:bg-purple-50 rounded-lg transition-colors" title="Print Invoice"><PrinterIcon className="w-4 h-4 text-purple-600" /></button>
        <button onClick={() => openEdit(c)}      className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"><PencilIcon className="w-4 h-4 text-indigo-600" /></button>
        <button onClick={() => handleDelete(c._id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"><TrashIcon className="w-4 h-4 text-red-500" /></button>
      </div>
    )},
  ];

  if (!project && loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!project) return (
    <div className="text-center py-16 text-gray-400">Project not found</div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/admin/projects')}
          className="mt-1 p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-sm text-gray-400 mt-0.5">{project.client || 'No client'} · {project.description || ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge status={project.status} />
              <Badge status={project.priority} />
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {[
              { label: 'Budget',    value: '₹' + Number(budgetedRevenue).toLocaleString('en-IN'), color: 'bg-blue-50 text-blue-700' },
              { label: 'Total Cost',value: '₹' + Number(totalCost).toLocaleString('en-IN'),       color: 'bg-red-50 text-red-600'  },
              { label: 'Gross P&L', value: (grossProfit >= 0 ? '+' : '') + '₹' + Number(grossProfit).toLocaleString('en-IN'), color: grossProfit >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600' },
              { label: 'Progress',  value: (project.progress || 0) + '%',   color: 'bg-indigo-50 text-indigo-700' },
            ].map(stat => (
              <div key={stat.label} className={'rounded-xl p-3 ' + stat.color}>
                <p className="text-xs font-medium opacity-70">{stat.label}</p>
                <p className="text-lg font-bold mt-0.5">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ' +
                (tab === t.id ? 'border-indigo-600 text-indigo-600 bg-indigo-50/30' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Overview tab ── */}
        {tab === 'overview' && (
          <div className="p-6 space-y-5">
            {/* ── Stage Flowchart ── */}
            <ProjectStageFlowchart
              projectId={project._id}
              stage={project.stage || 'initiation'}
              onStageChange={async (newStage) => {
                try {
                  await projectsAPI.update(project._id, { stage: newStage });
                  await loadProject();
                  toast.success('Stage updated to ' + newStage);
                } catch {
                  toast.error('Failed to update stage');
                }
              }}
            />

            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Project Details</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ['Manager',    project.manager?.name || '—'],
                    ['Client',     project.client || '—'],
                    ['Start Date', project.startDate ? format(new Date(project.startDate), 'dd MMM yyyy') : '—'],
                    ['End Date',   project.endDate   ? format(new Date(project.endDate),   'dd MMM yyyy') : '—'],
                    ['Budget',     '₹' + Number(project.budget || 0).toLocaleString('en-IN')],
                    ['Members',    (project.members?.length || 0) + ' people'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 border-b border-gray-50">
                      <span className="text-gray-400">{k}</span>
                      <span className="font-medium text-gray-800">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Cost Breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Material Cost',     value: materialCost, icon: CubeIcon,      color: 'text-orange-600 bg-orange-50' },
                    { label: 'Sub-Contractor Cost',value: subconCost,   icon: UserGroupIcon,  color: 'text-purple-600 bg-purple-50' },
                    { label: 'Total Cost',         value: totalCost,    icon: ArrowDownCircleIcon, color: 'text-red-600 bg-red-50' },
                    { label: 'Total Paid',         value: totalPaid,    icon: CheckCircleIcon,     color: 'text-green-600 bg-green-50' },
                    { label: 'Outstanding',        value: outstanding,  icon: DocumentTextIcon,    color: 'text-amber-600 bg-amber-50' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={'w-8 h-8 rounded-lg flex items-center justify-center ' + item.color}>
                          <item.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item.label}</span>
                      </div>
                      <span className="font-bold text-gray-900">₹{Number(item.value).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>

                {/* Add cost buttons */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <Button icon={CubeIcon} variant="secondary" onClick={() => openCreate('material')} className="w-full justify-center">
                    Add Material
                  </Button>
                  <Button icon={UserGroupIcon} variant="secondary" onClick={() => openCreate('subcontractor')} className="w-full justify-center">
                    Add Sub-Con
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Material / Sub-contractor tabs ── */}
        {(tab === 'material' || tab === 'subcontractor') && (
          <div>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800 text-sm">
                  {tab === 'material' ? 'Material Invoices' : 'Sub-Contractor Invoices'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {filteredCosts.length} entries ·
                  Total: ₹{filteredCosts.reduce((s, c) => s + Number(c.total || 0), 0).toLocaleString('en-IN')}
                </p>
              </div>
              <Button icon={PlusIcon} onClick={() => openCreate(tab)}>
                Add {tab === 'material' ? 'Material' : 'Sub-Contractor'} Invoice
              </Button>
            </div>

            {/* Cost summary cards */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b border-gray-100">
              {[
                { label: 'Total Value', value: filteredCosts.reduce((s, c) => s + Number(c.total || 0), 0),      color: 'indigo' },
                { label: 'Total Paid',  value: filteredCosts.reduce((s, c) => s + Number(c.paidAmount || 0), 0), color: 'green'  },
                { label: 'Outstanding', value: filteredCosts.reduce((s, c) => s + Math.max(0, Number(c.total || 0) - Number(c.paidAmount || 0)), 0), color: 'amber' },
              ].map(stat => (
                <div key={stat.label} className={`rounded-xl p-3 bg-${stat.color}-50 border border-${stat.color}-100`}>
                  <p className={`text-xs font-medium text-${stat.color}-600`}>{stat.label}</p>
                  <p className={`text-lg font-bold text-${stat.color}-700 mt-0.5`}>
                    ₹{Number(stat.value).toLocaleString('en-IN')}
                  </p>
                </div>
              ))}
            </div>

            <Table columns={costCols} data={filteredCosts} loading={loading}
              emptyMessage={'No ' + (tab === 'material' ? 'material' : 'sub-contractor') + ' invoices yet. Click Add to create one.'} />
          </div>
        )}

        {/* ── P&L Summary tab ── */}
        {tab === 'pl' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Profit & Loss Statement</h3>
              <p className="text-xs text-gray-400 mb-5">Project: {project.name} · {project.client || 'No client'}</p>

              {/* P&L Table */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Description</th>
                      <th className="text-right px-5 py-3 font-semibold text-gray-600">Amount (₹)</th>
                      <th className="text-right px-5 py-3 font-semibold text-gray-600">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Revenue */}
                    <tr className="bg-blue-50/50">
                      <td className="px-5 py-3 font-bold text-blue-800 text-base" colSpan="3">INCOME / REVENUE</td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-5 py-3 pl-8 text-gray-700">Project Budget / Contract Value</td>
                      <td className="px-5 py-3 text-right font-semibold text-blue-700">₹{Number(budgetedRevenue).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-right text-gray-400">100%</td>
                    </tr>

                    {/* Costs */}
                    <tr className="bg-red-50/50">
                      <td className="px-5 py-3 font-bold text-red-800 text-base" colSpan="3">COSTS / EXPENDITURE</td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-5 py-3 pl-8 text-gray-700">
                        <div className="flex items-center gap-2">
                          <CubeIcon className="w-4 h-4 text-orange-500" />
                          Material Cost
                        </div>
                        <p className="text-xs text-gray-400 pl-6 mt-0.5">
                          {summary.find(s => s._id === 'material')?.count || 0} invoice(s)
                        </p>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-red-600">₹{Number(materialCost).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-right text-gray-400">
                        {budgetedRevenue > 0 ? ((materialCost / budgetedRevenue) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-5 py-3 pl-8 text-gray-700">
                        <div className="flex items-center gap-2">
                          <UserGroupIcon className="w-4 h-4 text-purple-500" />
                          Sub-Contractor Cost
                        </div>
                        <p className="text-xs text-gray-400 pl-6 mt-0.5">
                          {summary.find(s => s._id === 'subcontractor')?.count || 0} invoice(s)
                        </p>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-red-600">₹{Number(subconCost).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-right text-gray-400">
                        {budgetedRevenue > 0 ? ((subconCost / budgetedRevenue) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>

                    {/* Total Cost */}
                    <tr className="border-t-2 border-gray-300 bg-gray-50">
                      <td className="px-5 py-3 font-bold text-gray-800">Total Cost</td>
                      <td className="px-5 py-3 text-right font-bold text-red-600">₹{Number(totalCost).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-right font-bold text-gray-500">
                        {budgetedRevenue > 0 ? ((totalCost / budgetedRevenue) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>

                    {/* Gross Profit */}
                    <tr className={'border-t-2 border-gray-400 ' + (grossProfit >= 0 ? 'bg-green-50' : 'bg-red-50')}>
                      <td className={'px-5 py-4 font-bold text-lg ' + (grossProfit >= 0 ? 'text-green-800' : 'text-red-800')}>
                        {grossProfit >= 0 ? '🟢' : '🔴'} Gross {grossProfit >= 0 ? 'Profit' : 'Loss'}
                      </td>
                      <td className={'px-5 py-4 text-right font-bold text-xl ' + (grossProfit >= 0 ? 'text-green-700' : 'text-red-600')}>
                        {grossProfit >= 0 ? '+' : ''}₹{Number(grossProfit).toLocaleString('en-IN')}
                      </td>
                      <td className={'px-5 py-4 text-right font-bold text-lg ' + (grossProfit >= 0 ? 'text-green-600' : 'text-red-500')}>
                        {profitPct}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment Status */}
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Cost',     value: totalCost,    color: 'red',    icon: ArrowDownCircleIcon },
                  { label: 'Total Paid',     value: totalPaid,    color: 'green',  icon: CheckCircleIcon },
                  { label: 'Outstanding',    value: outstanding,  color: 'amber',  icon: DocumentTextIcon },
                  { label: 'Profit Margin',  value: profitPct + '%', raw: true, color: grossProfit >= 0 ? 'green' : 'red', icon: ChartBarIcon },
                ].map(stat => (
                  <div key={stat.label} className={`bg-${stat.color}-50 border border-${stat.color}-100 rounded-2xl p-4`}>
                    <div className="flex items-center gap-2 mb-1">
                      <stat.icon className={`w-4 h-4 text-${stat.color}-600`} />
                      <p className={`text-xs font-semibold text-${stat.color}-600`}>{stat.label}</p>
                    </div>
                    <p className={`text-xl font-bold text-${stat.color}-700`}>
                      {stat.raw ? stat.value : '₹' + Number(stat.value).toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>

              {/* Material breakdown table */}
              {costs.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm">All Invoices Breakdown</h4>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400">Title</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400">Type</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400">Party</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-400">Total</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-400">Paid</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-400">Balance</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {costs.map((c, i) => {
                          const balance = Number(c.total || 0) - Number(c.paidAmount || 0);
                          return (
                            <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-4 py-2.5">
                                <p className="font-medium text-gray-800">{c.title}</p>
                                <p className="text-xs font-mono text-gray-400">{c.invoiceNo || '—'}</p>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' +
                                  (c.costType === 'material' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700')}>
                                  {c.costType === 'material' ? '📦 Material' : '👷 Sub-Con'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 text-xs">{c.vendor?.name || c.vendorName || '—'}</td>
                              <td className="px-4 py-2.5 text-right font-semibold text-gray-900">₹{Number(c.total || 0).toLocaleString('en-IN')}</td>
                              <td className="px-4 py-2.5 text-right text-green-600">₹{Number(c.paidAmount || 0).toLocaleString('en-IN')}</td>
                              <td className={'px-4 py-2.5 text-right font-semibold ' + (balance > 0 ? 'text-amber-600' : 'text-green-600')}>
                                ₹{Math.max(0, balance).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-2.5 text-center"><Badge status={c.status} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                        <tr>
                          <td colSpan="3" className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">
                            ₹{costs.reduce((s, c) => s + Number(c.total || 0), 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-green-700">
                            ₹{costs.reduce((s, c) => s + Number(c.paidAmount || 0), 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-amber-700">
                            ₹{costs.reduce((s, c) => s + Math.max(0, Number(c.total || 0) - Number(c.paidAmount || 0)), 0).toLocaleString('en-IN')}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Project Docs tab ── */}
        {tab === 'docs' && (
          <div className="p-6 space-y-5">

            {/* Header row */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800">Project Documents</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  All documents grouped by stage · {allDocs.length} total
                </p>
              </div>
              <button
                onClick={loadAllDocs}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                ↻ Refresh
              </button>
            </div>

            {docsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-7 h-7 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : allDocs.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <FolderOpenIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No documents uploaded yet.</p>
                <p className="text-xs mt-1">Go to Overview → click any stage node to upload documents.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {PROJECT_STAGES.map(stageDef => {
                  const stageDocs = allDocs.filter(d => d.stage === stageDef.key);
                  if (stageDocs.length === 0) return null;
                  return (
                    <div key={stageDef.key} className="rounded-2xl border border-gray-100 overflow-hidden">
                      {/* Stage header */}
                      <div className={`flex items-center justify-between px-4 py-3 ${stageDef.bg} border-b border-black/5`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-white/60`}>
                            <stageDef.icon className={`w-4 h-4 ${stageDef.color}`} />
                          </div>
                          <span className={`text-sm font-bold ${stageDef.color}`}>{stageDef.label}</span>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-white/60 ${stageDef.color}`}>
                          {stageDocs.length} file{stageDocs.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Document rows */}
                      <div className="divide-y divide-gray-50 bg-white">
                        {stageDocs.map(doc => {
                          const ext  = (doc.name || '').split('.').pop().toLowerCase();
                          const EXT_CFG = {
                            pdf:  { Icon: DocumentTextIcon, color: 'text-red-500',    bg: 'bg-red-50'    },
                            doc:  { Icon: DocumentTextIcon, color: 'text-blue-500',   bg: 'bg-blue-50'   },
                            docx: { Icon: DocumentTextIcon, color: 'text-blue-500',   bg: 'bg-blue-50'   },
                            xls:  { Icon: TableCellsIcon,   color: 'text-green-500',  bg: 'bg-green-50'  },
                            xlsx: { Icon: TableCellsIcon,   color: 'text-green-500',  bg: 'bg-green-50'  },
                            jpg:  { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
                            jpeg: { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
                            png:  { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
                          };
                          const cfg = EXT_CFG[ext] || { Icon: FolderIcon, color: 'text-gray-500', bg: 'bg-gray-100' };
                          const fmtSize = (bytes) => {
                            if (bytes < 1024) return bytes + ' B';
                            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                          };
                          const handleDownload = () => {
                            const url = `/api/projects/${id}/documents/${doc._id}/download`;
                            const a = document.createElement('a');
                            a.href = url; a.download = doc.name; a.target = '_blank';
                            document.body.appendChild(a); a.click(); a.remove();
                          };
                          const handleDelete = async () => {
                            if (!window.confirm(`Delete "${doc.name}"?`)) return;
                            try {
                              await projectDocumentsAPI.remove(id, doc._id);
                              toast.success('Deleted');
                              setAllDocs(prev => prev.filter(d => d._id !== doc._id));
                            } catch { toast.error('Delete failed'); }
                          };

                          return (
                            <div key={doc._id}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group">
                              {/* File icon */}
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                                <cfg.Icon className={`w-5 h-5 ${cfg.color}`} />
                              </div>

                              {/* File info */}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-800 truncate">{doc.name}</p>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <span className="text-xs text-gray-400">{fmtSize(doc.size)}</span>
                                  <span className="text-gray-200">·</span>
                                  <span className="text-xs text-gray-400">
                                    {new Date(doc.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                  {doc.uploadedBy?.name && (
                                    <>
                                      <span className="text-gray-200">·</span>
                                      <span className="text-xs text-gray-400">{doc.uploadedBy.name}</span>
                                    </>
                                  )}
                                </div>
                                {doc.note && (
                                  <p className="text-xs text-gray-500 mt-0.5 italic">"{doc.note}"</p>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <button onClick={handleDownload}
                                  className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors" title="Download">
                                  <ArrowDownTrayIcon className="w-4 h-4 text-indigo-500" />
                                </button>
                                <button onClick={handleDelete}
                                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                  <TrashIcon className="w-4 h-4 text-red-400" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cost Entry Form */}
      <ProjectCostInvoiceModal
        isOpen={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        record={invoiceRecord}
        invoiceType={invoiceType}
        projectName={project?.name}
      />

      <CostEntryForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        editing={editing}
        costType={formType}
      />
    </div>
  );
}